import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import cx from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  ThunkGetChatMessages,
  ThunkSendChatAttachment,
  ThunkSendMessage,
} from 'store/chats/thunks';
import { ChatsSelector, MessagesSelector, TechTypingSelector } from 'store/chats/selectors';
import { addNewMessage, updateMessageState } from 'store/chats';
import { UserDataSelector } from 'store/user/selector';
import { formatMessage } from 'utils/messageFormatting';

import { getFormattedDate } from 'utils/dates';

import Loader from 'components/Loader';
import Input from 'components/Input/Input';
import MessageWithAttachment from 'pages/Chats/components/MessageWithAttachment';
import FileSelector from 'components/FileSelector/FileSelector';

import { ReactComponent as CheckIcon } from 'assets/check.icon.svg';
import { ReactComponent as SendIcon } from 'assets/send.icon.svg';
import TypingAnimation from 'pages/Chats/components/TypingAnimation';
import { IChat, IChatMessage } from 'services/chats/types';

import useStyles from '../styles';
import { tt } from '@i18n/tt';
import { useParams } from 'react-router-dom';
import { removeNotification } from '../../../store/notifications';
import { NotificationsSelector } from '../../../store/notifications/selectors';

interface IProps {
  activeChatId: number;
  isTicketClosed?: boolean;
}

const ChatBody: React.FC<IProps> = ({ activeChatId, isTicketClosed = false }) => {
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState<IChat | null>(null);

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const messages = useAppSelector(MessagesSelector);
  const user = useAppSelector(UserDataSelector);
  const chats = useAppSelector(ChatsSelector);
  const techTyping = useAppSelector(TechTypingSelector);
  const notifications = useAppSelector(NotificationsSelector);
  const handleSendAttachment = useCallback(
    async (file: File) => {
      if (activeChatId) {
        const id = new Date().getTime();

        const msg: IChatMessage = {
          id,
          dateTime: new Date().toISOString(),
          message: file.name,
          status: 'loading',
          contactName: user.contactName,
          resourceName: null,
          metadata: JSON.stringify({
            FileName: file.name,
            Size: file.size,
            Path: null,
            ContentType: file.type,
            ThumbPath: URL.createObjectURL(file),
          }),
          type: 0,
        };
        dispatch(addNewMessage(msg));

        await dispatch(ThunkSendChatAttachment({ chatId: activeChatId, file }))
          .unwrap()
          .then(() => {
            dispatch(updateMessageState({ messageId: id, status: undefined }));
          });
      }
    },
    [activeChatId, dispatch],
  );

  const formik = useFormik({
    initialValues: {
      text: '',
    },
    onSubmit: values => {
      formik.setFieldValue('text', '');

      const id = new Date().getTime();

      const msg: IChatMessage = {
        id,
        dateTime: new Date().toISOString(),
        message: values.text,
        status: 'loading',
        contactName: user.contactName,
        resourceName: null,
        metadata: null,
        type: 0,
      };

      dispatch(addNewMessage(msg));

      dispatch(ThunkSendMessage({ payload: { chatId: +activeChatId, message: values.text } }))
        .unwrap()
        .finally(() => {
          dispatch(updateMessageState({ messageId: id, status: undefined }));
        });
    },
  });

  const onMount = useCallback(() => {
    const chat = chats.find(chat => +chat?.id === +activeChatId);

    if (chat) {
      setSelectedChat(chat);
    }

    dispatch(ThunkGetChatMessages({ chatId: +activeChatId }))
      .unwrap()
      .finally(() => {
        setLoading(false);
      });
  }, [activeChatId, chats, dispatch]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    if (lastMessageRef?.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [messages, lastMessageRef, loading]);

  useEffect(() => {
    const hasUnreadNotifications = notifications.find((notification) => notification.chatId === activeChatId);
    if (hasUnreadNotifications) {
      dispatch(removeNotification(activeChatId));
    }
  }, [notifications, activeChatId])

  if (loading) return <Loader />;
  return (
    <div className={classes.chatBodyWrapper}>
      <div className={classes.chatBody}>
        {messages.map(message => (
          <div
            key={message.id}
            className={cx(classes.message, {
              [classes.myMessage]: message.contactName,
              [classes.incomingMessage]: message.resourceName,
            })}
          >
            {message.resourceName && (
              <div className={classes.messageSender}>{message.resourceName}</div>
            )}
            {!message.metadata ? (
              formatMessage(message.message)
            ) : (
              <MessageWithAttachment metadata={message.metadata} />
            )}

            <div
              className={cx(classes.date, {
                [classes.myMessageDate]: message.contactName,
              })}
            >
              {message.status === 'loading' ? 'Sending...' : getFormattedDate(message.dateTime)}
            </div>
          </div>
        ))}
        <div>{techTyping.IsTyping && <TypingAnimation />}</div>
        <div ref={lastMessageRef}> </div>
      </div>

      {!isTicketClosed && !selectedChat?.isClosed ? (
        <form onSubmit={formik.handleSubmit}>
          <Input
            formik={formik}
            name="text"
            label={tt('common.typeMessage')}
            InputProps={{
              endAdornment: (
                <div className={classes.sendIcon}>
                  <FileSelector
                    onFileSelection={(file: File) => {
                      handleSendAttachment(file);
                    }}
                  />
                  <SendIcon
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      formik.handleSubmit();
                    }}
                  />
                </div>
              ),
            }}
          />
        </form>
      ) : (
        <div className={classes.chatResolved}>
          <CheckIcon /> {tt('pages.chats.resolvedChat')}
        </div>
      )}
    </div>
  );
};

export default ChatBody;
